import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";

import { api, ApiError } from "./api.js";

/* ============================================================================
   כניסה בטביעת אצבע, בצד הדפדפן.

   הדפדפן הוא זה שמדבר עם החיישן; אנחנו רק מעבירים אתגר מהשרת ומחזירים
   חתימה. טביעת האצבע לא עוברת דרך הקוד הזה ולא ניתנת לקריאה ממנו.

   שתי דרישות סביבה שכדאי להכיר: הדף חייב להיות בהקשר מאובטח (https, או
   localhost בפיתוח), והדומיין חייב להתאים ל-PUBLIC_URL שבשרת.
   ============================================================================ */

export const supported = () => browserSupportsWebAuthn();

/* האם במכשיר הזה אי פעם הוגדרה או שימשה כניסה מהירה. WebAuthn לא מאפשר
   לשאול את הדפדפן "יש כאן passkey?", ולכן נרשם דגל מקומי בהצלחה של
   הגדרה או כניסה — וכפתור טביעת האצבע במסך הכניסה מוצג רק כשהוא דולק,
   במקום לכל מי שיש לו חיישן במכשיר. */
const KNOWN_KEY = "rentals:passkey-known";
export const knownOnDevice = () => {
  try { return localStorage.getItem(KNOWN_KEY) === "1"; } catch { return false; }
};
const noteKnown = () => {
  try { localStorage.setItem(KNOWN_KEY, "1"); } catch { /* מצב פרטי */ }
};
export const forgetKnown = () => {
  try { localStorage.removeItem(KNOWN_KEY); } catch { /* מצב פרטי */ }
};

/** האם יש חיישן מובנה במכשיר (טביעת אצבע / זיהוי פנים), ולא רק מפתח USB. */
export async function hasDeviceSensor() {
  if (!browserSupportsWebAuthn()) return false;
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

/**
 * שגיאות הדפדפן כאן הן DOMException עם שמות טכניים. NotAllowedError הוא
 * גם ביטול על ידי המשתמש וגם timeout — ובשני המקרים אין מה להתריע.
 */
function toMessage(err) {
  if (err instanceof ApiError) return err.message;
  const name = err?.name || "";
  if (name === "NotAllowedError") return null;            // בוטל, לא שגיאה
  if (name === "InvalidStateError") return "המכשיר הזה כבר מוגדר לכניסה מהירה.";
  if (name === "NotSupportedError") return "המכשיר הזה לא תומך בכניסה מהירה.";
  if (name === "SecurityError") return "כניסה מהירה עובדת רק בחיבור מאובטח (https).";
  return "הכניסה המהירה נכשלה. אפשר להיכנס עם סיסמה.";
}

/** שגיאה שכבר נוסחה למשתמש. message ריק = המשתמש ביטל, אין מה להציג. */
export class PasskeyError extends Error {
  constructor(message) {
    super(message || "");
    this.name = "PasskeyError";
    this.cancelled = !message;
  }
}

/** מגדיר את המכשיר הנוכחי ככניסה מהירה. דורש משתמש מחובר. */
export async function enroll(label) {
  try {
    const options = await api.post("/webauthn/register/options");
    const response = await startRegistration({ optionsJSON: options });
    const r = await api.post("/webauthn/register/verify", { response, label });
    noteKnown();
    return r;
  } catch (err) {
    throw new PasskeyError(toMessage(err));
  }
}

/**
 * כניסה. אין צורך באימייל — המכשיר יודע איזה חשבון שמור עליו.
 * מחזיר { ok, email } ומשאיר עוגיית סשן, בדיוק כמו כניסה עם סיסמה.
 */
export async function login() {
  try {
    const options = await api.post("/webauthn/login/options");
    const response = await startAuthentication({ optionsJSON: options });
    const r = await api.post("/webauthn/login/verify", { response });
    noteKnown();
    return r;
  } catch (err) {
    throw new PasskeyError(toMessage(err));
  }
}

export const list = () => api.get("/webauthn/credentials");
export const remove = (id) => api.del(`/webauthn/credentials/${encodeURIComponent(id)}`);
